import { useEffect, useMemo, useRef } from "react";
import type { DayView } from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useLibraryStore } from "../../store/libraryStore";
import { computeDayViews } from "../../utils/dayViews";
import { blockStartTime } from "../../utils/dayViews";
import {
  DEFAULT_SCROLL_HOUR,
  HOUR_HEIGHT_PX,
  columnHeightPx,
  instantToY,
} from "../../utils/timeGrid";
import { buildAccommodationContext } from "./accommodationContext";
import { CalendarDayHeader } from "./CalendarDayHeader";
import { CalendarDayColumn } from "./CalendarDayColumn";
import { AccommodationBanner } from "./AccommodationBanner";
import { TimeAxis } from "./TimeAxis";
import { WeekAllDayRail } from "./WeekAllDayRail";
import { CalendarLegBand } from "./CalendarLegBand";
import { useLegStore } from "../../store/legStore";
import type { DayAccommodationContext } from "./types";

/**
 * The week-style trip grid: every trip day as a side-by-side column over a
 * shared vertical hour axis, with day headers pinned to the top and
 * accommodation banners pinned to the bottom. Read rendering — editing
 * gestures arrive with milestone 3.
 */
interface CalendarWeekViewProps {
  onShowOnMap: (poiId: string) => void;
  dayViews?: DayView[];
  contextKey?: string;
}

export function CalendarWeekView({
  onShowOnMap,
  dayViews: selectedDayViews,
  contextKey,
}: CalendarWeekViewProps) {
  const trip = useTripStore((s) => s.trip);
  const schedule = useTripStore((s) => s.schedule);
  const isReadOnly = useTripStore((s) => s.isReadOnly);
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);
  const getAccommodation = useLibraryStore((s) => s.getAccommodation);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const allDayViews = useMemo(
    () => (trip ? computeDayViews(trip, schedule) : []),
    [trip, schedule],
  );
  const dayViews = selectedDayViews ?? allDayViews;

  const accommodationByDate = useMemo(() => {
    const contexts = new Map<string, DayAccommodationContext>();
    for (const dayView of dayViews) {
      contexts.set(
        dayView.date,
        buildAccommodationContext(dayView.date, schedule, getAccommodation),
      );
    }
    return contexts;
  }, [dayViews, schedule, getAccommodation]);

  const maxColumnHeight = useMemo(
    () =>
      dayViews.reduce(
        (max, dayView) =>
          Math.max(max, columnHeightPx(dayView.date, dayView.zone)),
        24 * HOUR_HEIGHT_PX,
      ),
    [dayViews],
  );
  const hasAllDayNotes = dayViews.some(
    (dayView) => dayView.allDayBlocks.length > 0,
  );
  // The band shows whenever a leg touches these dates, and stays available to
  // editors even with no legs yet so the "add leg" control is reachable.
  const legs = useLegStore((state) => state.legs);
  const firstDate = dayViews[0]?.date;
  const lastDate = dayViews[dayViews.length - 1]?.date;
  const showLegBand =
    !isReadOnly() ||
    legs.some(
      (leg) =>
        firstDate &&
        lastDate &&
        leg.startDate <= lastDate &&
        leg.endDate >= firstDate,
    );

  // Initial scroll: anchor to the trip's earliest block, fallback 08:00
  useEffect(() => {
    const scroller = scrollRef.current;
    const body = bodyRef.current;
    if (!scroller || !body) return;

    let anchorY = DEFAULT_SCROLL_HOUR * HOUR_HEIGHT_PX;
    for (const dayView of dayViews) {
      const first = dayView.blocks[0];
      if (!first) continue;
      const y = instantToY(blockStartTime(first), dayView.date, dayView.zone);
      anchorY = Math.min(anchorY, Math.max(y, 0));
    }
    scroller.scrollTop = Math.max(body.offsetTop + anchorY - 16, 0);
    // Re-anchor only when switching trips — not on every schedule edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id, contextKey]);

  if (!trip) return null;

  return (
    <div
      ref={scrollRef}
      className="custom-scrollbar relative flex-1 overflow-auto bg-page max-md:snap-x max-md:snap-proximity"
      data-testid="calendar-week-view"
    >
      <div
        className="grid gap-x-2.5 px-4"
        style={{
          gridTemplateColumns: `56px repeat(${dayViews.length}, minmax(208px, 1fr))`,
          minWidth: 56 + dayViews.length * 216,
        }}
      >
        {/* Header + all-day ranges share one sticky surface so ranges align. */}
        <div
          className="sticky top-0 z-20 grid gap-x-2.5 bg-page pb-2 pt-3"
          style={{
            gridColumn: "1 / -1",
            gridTemplateColumns: `56px repeat(${dayViews.length}, minmax(208px, 1fr))`,
          }}
        >
          <div />
          {dayViews.map((dayView, index) => (
            <CalendarDayHeader
              key={dayView.date}
              date={dayView.date}
              dayIndex={index}
              zone={dayView.zone}
              defaultTimezone={trip.defaultTimezone}
              isReadOnly={isReadOnly()}
            />
          ))}
          {(hasAllDayNotes || showLegBand) && (
            <>
              <div className="flex items-start justify-end px-1 pt-2 text-2xs font-extrabold uppercase tracking-wider text-tertiary">
                All day
              </div>
              <div
                className="flex flex-col gap-1 pt-2"
                style={{ gridColumn: `2 / ${dayViews.length + 2}` }}
              >
                {/* Legs sit in the all-day area, above any all-day notes. */}
                {showLegBand && (
                  <CalendarLegBand
                    dayViews={dayViews}
                    isReadOnly={isReadOnly()}
                  />
                )}
                {hasAllDayNotes && (
                  <WeekAllDayRail
                    dayViews={dayViews}
                    isReadOnly={isReadOnly()}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Body row: time gutter + day canvases */}
        <div ref={bodyRef}>
          <TimeAxis heightPx={maxColumnHeight} />
        </div>
        {dayViews.map((dayView) => (
          <CalendarDayColumn
            key={dayView.date}
            dayView={dayView}
            accommodation={accommodationByDate.get(dayView.date)!}
            pois={pois}
            tags={tags}
            isReadOnly={isReadOnly()}
            onShowOnMap={onShowOnMap}
          />
        ))}

        {/* Footer row (sticky bottom): where you sleep tonight */}
        <div className="sticky bottom-0 z-20 bg-page" />
        {dayViews.map((dayView) => (
          <div
            key={dayView.date}
            className="sticky bottom-0 z-20 bg-page pb-3 pt-2"
          >
            <AccommodationBanner
              date={dayView.date}
              accommodation={accommodationByDate.get(dayView.date)!}
              isReadOnly={isReadOnly()}
              onShowOnMap={onShowOnMap}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
